// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useEffect } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { apiClient, isSessionDenied, setSessionExpiredHandler } from "@/queries/client"
import { USER_SELF_QUERY_KEY, useUserSelf } from "@/queries/queries.users"

export type SessionStatus =
  | "pending"
  | "authenticated"
  | "anonymous"
  | "indeterminate"

// The single source of auth truth for the SPA: identity comes from the
// session probe riding the HttpOnly cookie, whose 401 is the normal
// anonymous answer rather than an error.
export const useSession = (): {
  status: SessionStatus
  reprobe: () => void
} => {
  const { data, isPending, isFetching, isError, error, refetch } = useUserSelf()

  const read = (): SessionStatus => {
    // A re-ask in flight is still an open question — answering "anonymous"
    // meanwhile would bounce the visitor through the login page.
    if (isPending || (isFetching && !data)) return "pending"
    // A denied probe outranks the identity it returned before the denial:
    // that user is no longer signed in. A probe that failed for any other
    // reason answered nothing at all, so the session is unknown rather
    // than absent — sending that visitor to login would sign them out of
    // a session they still hold.
    if (isError) return isSessionDenied(error) ? "anonymous" : "indeterminate"
    return data ? "authenticated" : "anonymous"
  }

  return {
    status: read(),
    reprobe: () => {
      void refetch()
    },
  }
}

// `onSettled`, not `onSuccess`: a visitor who asked to leave, leaves, so
// the cache drop and the departure run even when the Backend refuses.
export const useLogout = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: () => apiClient.post("/auth/logout"),
    onSettled: () => {
      queryClient.clear()
      navigate("/login", { replace: true })
    },
  })
}

// Turning a rejected cookie into a re-probe lets the guard route on the
// answer, so expiry and cold arrival take the same path.
export const useSessionExpiry = (): void => {
  const queryClient = useQueryClient()
  useEffect(() => {
    setSessionExpiredHandler(() => {
      void queryClient.invalidateQueries({ queryKey: USER_SELF_QUERY_KEY })
    })
    return () => setSessionExpiredHandler(null)
  }, [queryClient])
}
